import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LineChart } from "lucide-react";
import PriceChart from "@/components/PriceChart";

const Stat = ({ label, value, testid }) => (
  <div><div className="text-xs text-slate-500">{label}</div><div className="text-2xl font-display font-bold text-[var(--navy)]" data-testid={testid}>{value}</div></div>
);

export default function Invest() {
  const [pf, setPf] = useState(null);
  const [mkt, setMkt] = useState([]);
  const [qty, setQty] = useState({});
  const [charts, setCharts] = useState({});
  const load = () => { api.get("/portfolio").then((r) => setPf(r.data)); api.get("/market").then((r) => setMkt(r.data)); };
  useEffect(() => { load(); }, []);

  const trade = async (symbol, side) => {
    const shares = parseFloat(qty[symbol]);
    if (!shares || shares <= 0) return toast.error("Enter number of shares");
    try {
      const r = await api.post("/trade", { symbol, side, shares });
      toast.success(`${side === "buy" ? "Bought" : "Sold"} ${shares} ${symbol} for ${money(r.data.total)}`);
      setQty((p) => ({ ...p, [symbol]: "" })); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!pf) return <div className="text-slate-500">Loading portfolio...</div>;
  if (!pf.account) return (
    <div className="tile max-w-xl" data-testid="no-investment-account"><div className="p-8">
      <h1 className="text-3xl font-extrabold text-[var(--navy)] mb-2">Investing</h1>
      <p className="text-slate-500">You don't have a Self-Directed Investing account yet. Contact Maderian to add one to your profile.</p>
    </div></div>
  );
  return (
    <div data-testid="invest-page">
      <h1 className="text-4xl sm:text-5xl font-extrabold text-[var(--navy)] mb-2">Investing</h1>
      <p className="text-slate-500 mb-8">Build and track your portfolio. Prices refresh hourly.</p>
      <div className="tile mb-8"><div className="p-7 grid grid-cols-2 md:grid-cols-3 gap-6">
        <Stat label="Total portfolio value" value={money(pf.total_value)} testid="portfolio-total" />
        <Stat label="Cash available to invest" value={money(pf.cash)} testid="portfolio-cash" />
        <Stat label="Holdings value" value={money(pf.holdings_value)} testid="portfolio-holdings-value" />
      </div></div>
      <h2 className="text-lg font-bold text-[var(--navy)] mb-4">Your holdings</h2>
      <div className="tile mb-10 overflow-x-auto" data-testid="holdings-table">
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500"><tr>{["Symbol", "Shares", "Avg cost", "Price", "Value", "Gain/Loss"].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y">
            {pf.holdings.map((h) => (
              <tr key={h.symbol} data-testid={`holding-${h.symbol}`}>
                <td className="px-5 py-3 font-semibold">{h.symbol}<div className="text-xs text-slate-500 font-normal">{h.name}</div></td>
                <td className="px-5 py-3">{h.shares}</td><td className="px-5 py-3">{money(h.avg_cost)}</td><td className="px-5 py-3">{money(h.price)}</td>
                <td className="px-5 py-3 font-semibold">{money(h.value)}</td>
                <td className={`px-5 py-3 font-semibold ${h.gain >= 0 ? "text-emerald-700" : "text-[var(--red)]"}`}>{money(h.gain)}</td>
              </tr>
            ))}
            {pf.holdings.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">No holdings yet. Buy your first shares below.</td></tr>}
          </tbody>
        </table>
      </div>
      <h2 className="text-lg font-bold text-[var(--navy)] mb-4">Market</h2>
      <div className="grid md:grid-cols-2 gap-4" data-testid="market-list">
        {mkt.map((s) => (
          <div key={s.symbol} className="tile" data-testid={`market-${s.symbol}`}>
            <div className="p-5 flex flex-wrap items-center gap-4 justify-between">
              <div className="min-w-[140px]"><div className="font-display font-bold text-[var(--navy)]">{s.symbol}</div><div className="text-xs text-slate-500">{s.name}</div></div>
              <div className="text-right"><div className="font-semibold">{money(s.price)}</div><div className={`text-xs font-semibold ${s.change_pct >= 0 ? "text-emerald-700" : "text-[var(--red)]"}`}>{s.change_pct >= 0 ? "+" : ""}{s.change_pct}%</div></div>
              <div className="flex gap-2 items-center w-full sm:w-auto">
                <Input type="number" min="0" step="1" placeholder="Qty" className="w-20" value={qty[s.symbol] || ""} onChange={(e) => setQty((p) => ({ ...p, [s.symbol]: e.target.value }))} data-testid={`qty-${s.symbol}`} />
                <Button size="sm" className="rounded-full bg-[var(--blue)] hover:bg-[var(--navy)]" onClick={() => trade(s.symbol, "buy")} data-testid={`buy-${s.symbol}`}>Buy</Button>
                <Button size="sm" variant="outline" className="rounded-full border-[var(--red)] text-[var(--red)]" onClick={() => trade(s.symbol, "sell")} data-testid={`sell-${s.symbol}`}>Sell</Button>
                <button onClick={() => setCharts((p) => ({ ...p, [s.symbol]: !p[s.symbol] }))} className={`p-2 rounded-full transition-colors ${charts[s.symbol] ? "bg-[var(--navy)] text-white" : "text-[var(--blue)] hover:bg-blue-50"}`} title="Price history" data-testid={`chart-toggle-${s.symbol}`}><LineChart size={16} /></button>
              </div>
            </div>
            {charts[s.symbol] && <PriceChart symbol={s.symbol} />}
          </div>
        ))}
      </div>
    </div>
  );
}
